import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar, Clock, User, Phone, CheckCircle2, XCircle, Ban, RefreshCw, Loader2 } from "lucide-react";
import { formatDateLong } from "@/lib/format";
import { toast } from "sonner";

export function BookingCard({ booking, viewAs, onChange }) {
  const [busy, setBusy] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/${action}`);
      toast.success(`Lesson ${action === "accept" ? "accepted" : action === "deny" ? "denied" : action === "complete" ? "completed" : action + "led"}`);
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openReschedule = async () => {
    setRescheduleOpen(true);
    setLoadingSlots(true);
    try {
      const { data } = await api.get(`/instructors/${booking.instructor_id}/slots`);
      setSlots(data);
    } catch (e) { toast.error("Could not load slots"); }
    finally { setLoadingSlots(false); }
  };

  const doReschedule = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/reschedule`, {
        date: selected.date, start_time: selected.start_time, end_time: selected.end_time,
      });
      toast.success("Lesson rescheduled — pending re-approval");
      setRescheduleOpen(false); setSelected(null);
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const canCancel = ["pending", "accepted"].includes(booking.status);
  const canReschedule = ["pending", "accepted"].includes(booking.status);
  const isInstructor = viewAs === "instructor";
  const counterpart = isInstructor ? booking.student_name : booking.instructor_name;

  return (
    <Card className="p-5 border-border rounded-2xl hover:shadow-[0_8px_32px_rgba(0,168,232,0.08)] transition-shadow duration-200" data-testid={`booking-card-${booking.booking_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-arctic-secondary font-heading font-semibold">
            <User className="w-4 h-4 text-arctic-primary shrink-0" />
            <span className="truncate">{counterpart}</span>
          </div>
          <div className="mt-2 space-y-1 text-sm text-muted-foreground">
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {formatDateLong(booking.date)}</div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4" /> {booking.start_time} – {booking.end_time}</div>
            {(isInstructor || viewAs === "admin") && booking.phone && (
              <div className="flex items-center gap-2 text-arctic-secondary font-medium" data-testid={`booking-phone-${booking.booking_id}`}><Phone className="w-4 h-4" /> {booking.phone}</div>
            )}
          </div>
          {booking.notes && <p className="mt-2 text-sm text-muted-foreground italic">“{booking.notes}”</p>}
        </div>
        <StatusBadge status={booking.status} />
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {isInstructor && booking.status === "pending" && (
          <>
            <Button size="sm" disabled={busy} onClick={() => act("accept")} data-testid={`accept-${booking.booking_id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Accept
            </Button>
            <Button size="sm" disabled={busy} variant="outline" onClick={() => act("deny")} data-testid={`deny-${booking.booking_id}`} className="rounded-full text-destructive border-destructive/30 hover:bg-destructive/10">
              <XCircle className="w-4 h-4 mr-1" /> Deny
            </Button>
          </>
        )}
        {isInstructor && booking.status === "accepted" && (
          <Button size="sm" disabled={busy} onClick={() => act("complete")} data-testid={`complete-${booking.booking_id}`} className="rounded-full bg-arctic-primary hover:bg-arctic-primary/90">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Mark complete
          </Button>
        )}
        {canReschedule && (
          <Button size="sm" disabled={busy} variant="outline" onClick={openReschedule} data-testid={`reschedule-${booking.booking_id}`} className="rounded-full">
            <RefreshCw className="w-4 h-4 mr-1" /> Reschedule
          </Button>
        )}
        {canCancel && (
          <Button size="sm" disabled={busy} variant="ghost" onClick={() => act("cancel")} data-testid={`cancel-${booking.booking_id}`} className="rounded-full text-muted-foreground hover:text-destructive">
            <Ban className="w-4 h-4 mr-1" /> Cancel
          </Button>
        )}
      </div>

      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule lesson</DialogTitle>
            <DialogDescription>Pick a new available slot. The other party will need to re-approve.</DialogDescription>
          </DialogHeader>
          {loadingSlots ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-arctic-primary" /></div>
          ) : slots.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No open slots available right now.</p>
          ) : (
            <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
              {slots.map((s, i) => {
                const active = selected && selected.date === s.date && selected.start_time === s.start_time;
                return (
                  <button key={i} onClick={() => setSelected(s)} data-testid={`reschedule-slot-${i}`}
                    className={`w-full flex items-center justify-between rounded-xl border px-4 py-2.5 text-sm transition-colors duration-200 ${active ? "border-arctic-primary bg-arctic-primary/5 ring-1 ring-arctic-primary" : "border-border hover:border-arctic-primary/50"}`}>
                    <span>{formatDateLong(s.date)}</span>
                    <span className="font-medium text-arctic-secondary">{s.start_time}–{s.end_time}</span>
                  </button>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRescheduleOpen(false)}>Cancel</Button>
            <Button disabled={!selected || busy} onClick={doReschedule} data-testid="confirm-reschedule" className="rounded-full bg-arctic-primary hover:bg-arctic-primary/90">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
